import { useEffect, useMemo, useRef } from "react";
import posthog from "posthog-js";
import { capture } from "../lib/analytics";
import { api } from "../lib/api";

/**
 * Returns the active variant for a feature flag with safe defaults.
 *
 * - If PostHog isn't loaded yet, returns the experiment's `default`.
 * - Once a variant is available it auto-fires `experiment_viewed` ONCE per
 *   experiment per session so exposure data is clean.
 *
 * Usage:
 *   const { variant, content } = useExperiment(EXPERIMENTS.LANDING_HEADLINE);
 *   <h1>{content.line1}</h1>
 */
export default function useExperiment(experiment) {
  const fired = useRef(false);

  // Resolve the variant
  const variant = useMemo(() => {
    try {
      const v = posthog?.getFeatureFlag?.(experiment.key);
      if (typeof v === "string" && experiment.variants[v]) return v;
    } catch {
      /* fall through */
    }
    return experiment.default;
  }, [experiment]);

  // Fire exposure once
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    capture("experiment_viewed", {
      experiment_key: experiment.key,
      variant,
    });
    // Also tell our own backend so the admin Hot Bar can compute conversion
    // rates per variant without needing the PostHog query API.
    api
      .post("/experiments/expose", { experiment_key: experiment.key, variant })
      .catch(() => {
        /* analytics must never break UX */
      });
  }, [experiment.key, variant]);

  const content = experiment.variants[variant] || experiment.variants[experiment.default];
  return { variant, content };
}

/**
 * Helper to enrich any conversion event with the user's exposure to a list
 * of experiments. Use when you want variant info attached to a specific
 * conversion event (e.g. checkout_start).
 */
export function variantsSnapshot(experimentList) {
  const out = {};
  try {
    for (const exp of experimentList) {
      const v = posthog?.getFeatureFlag?.(exp.key);
      out[`exp_${exp.key.replace(/-/g, "_")}`] =
        typeof v === "string" && exp.variants[v] ? v : exp.default;
    }
  } catch {
    /* ignore */
  }
  return out;
}
